import { useEffect, useState } from 'react';
import { addDeviationType, deleteDeviationType, DeviationType } from '../api';
import { api } from '../api/client';
import Modal from '../components/common/Modal';
import '../components/common/Modal.css';
import ConfirmDialog from '../components/common/ConfirmDialog';
import Spinner from '../components/common/Spinner';
import './Page.css';

const DeviationTypes = () => {
  const [deviations, setDeviations] = useState<DeviationType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [name, setName] = useState('');
  const [search, setSearch] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<DeviationType | null>(null);

  const load = () => {
    setLoading(true);
    api.get<DeviationType[]>('/admin/deviations')
      .then(setDeviations)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleAdd = async (e: { preventDefault(): void }) => {
    e.preventDefault();
    setFormError('');
    setSubmitting(true);
    try {
      await addDeviationType({ name });
      setShowModal(false);
      setName('');
      load();
    } catch (e: unknown) {
      setFormError(e instanceof Error ? e.message : 'Failed to add deviation type');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await deleteDeviationType(confirmDelete._id);
      setConfirmDelete(null);
      load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to delete deviation type');
      setConfirmDelete(null);
    }
  };

  const filtered = deviations.filter((d) =>
    d.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Deviation Types</h1>
          <p className="page-subtitle">{deviations.length} deviation gaps defined</p>
        </div>
        <button className="btn-primary" onClick={() => setShowModal(true)}>
          + Add Deviation Type
        </button>
      </div>

      <div className="page-toolbar">
        <input
          className="search-input"
          placeholder="Search deviation types..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {loading && <Spinner />}
      {error && <p className="error-banner">{error}</p>}

      {!loading && (
        <div className="table-card">
          <table className="data-table">
            <thead>
              <tr>
                <th>Deviation Name</th>
                <th>Created</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={3} className="empty-cell">No deviation types found</td></tr>
              ) : (
                filtered.map((d) => (
                  <tr key={d._id}>
                    <td><strong>{d.name}</strong></td>
                    <td>{new Date(d.createdAt).toLocaleDateString('en-GB')}</td>
                    <td>
                      <button className="btn-sm btn-danger" onClick={() => setConfirmDelete(d)}>Delete</button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <Modal title="Add Deviation Type" onClose={() => setShowModal(false)}>
          <form onSubmit={handleAdd}>
            {formError && <p className="form-error">{formError}</p>}
            <div className="form-field">
              <label>Deviation Name</label>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Missing SOP, Wrong Labelling" required />
            </div>
            <div className="form-actions">
              <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              <button type="submit" className="btn-primary" disabled={submitting}>{submitting ? 'Saving...' : 'Add'}</button>
            </div>
          </form>
        </Modal>
      )}

      {confirmDelete && (
        <ConfirmDialog
          title="Delete Deviation Type"
          message={`Are you sure you want to delete "${confirmDelete.name}"? This action cannot be undone.`}
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
};

export default DeviationTypes;
